import { SidebarBadgeTone } from './menuConfig'
import { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Tooltip } from 'components/ds2/Tooltip'
import { Badge } from 'components/ds2/Badge'

import { handleLinkClick } from 'utils/linkClick'
import { ROW_ACTIVE, ROW_BASE, ROW_DISABLED, ROW_IDLE } from './styles'

interface SidebarItemProps {
    icon: ReactNode
    label: string
    active?: boolean
    disabled?: boolean
    isOpen: boolean
    /** Optional badge shown to the right of the label */
    badge?: { label: string; tone: SidebarBadgeTone }
    /** Optional content rendered to the very right (e.g. an inline button) */
    suffix?: ReactNode
    /** Stable DOM hook used by overlays/tooltips to anchor to a specific row. */
    sidebarItemId?: string
    /** Real destination. When set (and the row is not disabled) the row renders as a link, so "open in new tab" works. */
    href?: string
    onClick?: () => void
}

/**
 * A single clickable row in the sidebar.
 *
 * Visual style mirrors the reference design (`new-agent-numeo-main`):
 * - 36px tall, 12px corner radius, 8px horizontal padding (`ROW_BASE`).
 * - Active state uses `ROW_ACTIVE` — same accent-soft background that
 *   `SidebarGroup` uses when one of its children is active, so headers and
 *   leaf rows light up identically.
 * - Hover state uses `ROW_IDLE` (`slate-100 / dark:slate-800`).
 * - When the sidebar is collapsed we render only the icon and surface the
 *   label as a right-side tooltip.
 */
export const SidebarItem = ({ icon, label, active, disabled, isOpen, badge, suffix, sidebarItemId, href, onClick }: SidebarItemProps) => {
    const rowClass = [ROW_BASE, isOpen ? 'justify-between' : 'justify-center', disabled ? ROW_DISABLED : active ? ROW_ACTIVE : ROW_IDLE].join(' ')
    const content = (
        <>
            <span className="flex min-w-0 items-center gap-2">
                <span className="flex shrink-0 items-center justify-center">{icon}</span>
                {isOpen && <span className="truncate">{label}</span>}
                {isOpen && badge && <Badge variant="soft" type={badge.tone} size="sm" label={badge.label} />}
            </span>
            {isOpen && suffix && <span className="flex shrink-0 items-center">{suffix}</span>}
        </>
    )
    const baseRow =
        href && !disabled ? (
            <Link
                to={href}
                onClick={(e) => handleLinkClick(e, onClick)}
                aria-current={active ? 'page' : undefined}
                data-sidebar-item={sidebarItemId}
                className={`${rowClass} no-underline`}
            >
                {content}
            </Link>
        ) : (
            <button
                type="button"
                onClick={disabled ? undefined : onClick}
                disabled={disabled}
                aria-current={active ? 'page' : undefined}
                data-sidebar-item={sidebarItemId}
                className={rowClass}
            >
                {content}
            </button>
        )

    if (!isOpen) {
        return (
            <Tooltip content={label} placement="right">
                {baseRow}
            </Tooltip>
        )
    }
    return baseRow
}
